import clsx from 'clsx'
import { type ReactNode, useEffect, useState } from 'react'
import { StarGroup } from '@/assets/icons/star-group'
import { ChooseCourses } from '@/components/buttons/choose-courses'
import { ChooseCurriculum } from '@/components/buttons/choose-curriculum'
import { ReviewCard } from '@/components/cards/review-card'
import { Show } from '@/components/show'
import { Button } from '@/components/ui/button'
import { Table, TableBody } from '@/components/ui/table'
import { REQUEST_STATUS } from '@/constants/request-status'
import type { Curriculum } from '@/types/courses/curriculum'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { PreviousEducationCourse } from '@/types/education/previous-education-course'
import type {
  StudentAutomaticCoursesFormMethods,
  StudentRequestedCoursesFormMethods
} from '@/types/forms/student-requested-courses-form-methods'
import type { Request } from '@/types/requests/request'
import { CoursesEquivalencesTableHeader } from './components/table-header'
import { CoursesEquivalencesTableRow } from './components/table-row'
import { useCourseEquivalence } from './hooks'

interface CoursesEquivalencesProps {
  currentCurriculum?: Curriculum
  setCurrentCurriculum?: (curriculum: Curriculum) => void
  curriculums?: Curriculum[]
  curriculumCourses?: DesiredEducationCourse[]
  request?: Request
  formMethods?: StudentRequestedCoursesFormMethods
  formMethodsIA?: StudentAutomaticCoursesFormMethods
  previousCourses?: PreviousEducationCourse[]
  onlyView?: boolean
  title?: string
  showCurriculumButton?: boolean
  showStatusColumn?: boolean
  variant?: 'student' | 'manager'
  showDisciplineButton?: boolean
  showCurriculum?: boolean
  footer?: ReactNode
}

export const CoursesEquivalences = ({
  currentCurriculum,
  curriculums,
  curriculumCourses,
  request,
  setCurrentCurriculum,
  formMethods,
  formMethodsIA,
  previousCourses,
  onlyView,
  title = 'Disciplinas e Equivalências Solicitadas pelo Aluno',
  showCurriculumButton = true,
  showStatusColumn,
  variant = 'manager',
  showDisciplineButton = true,
  showCurriculum = true,
  footer
}: CoursesEquivalencesProps) => {
  const [curriculumChanged, setCurriculumChanged] = useState(false)

  const {
    states: { isPendingDsiciplineAnalysis, alreadyAnalyzed },
    actions: { handleDisciplineAnalysis, canEditDesiredCourse }
  } = useCourseEquivalence({
    curriculumCourses,
    previousCourses,
    formMethods,
    currentCurriculum,
    request,
    variant
  })

  const onReview = request?.requestStatus?.id === REQUEST_STATUS.UNDER_REVIEW.id

  const isStudent = variant === 'student'
  const hasCourseEquivalences =
    formMethods?.getValues
    && (formMethods.getValues('courseEquivalences') || []).length > 0
  const hasAutomaticCourses =
    formMethods?.getValues
    && (formMethods.getValues('automaticCourseEquivalences') || []).length > 0

  const handleCurrculumChange = (curriculum: Curriculum) => {
    if (curriculum.id !== currentCurriculum?.id) {
      setCurrentCurriculum?.(curriculum)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!curriculumChanged && currentCurriculum) {
      if (currentCurriculum.id !== request?.desiredEducation?.curriculum) {
        setCurriculumChanged(true)
      }
    }
  }, [currentCurriculum])

  return (
    <div>
      <Show when={!!showCurriculum}>
        <div className="flex items-center pb-2 justify-between">
          <div className="flex gap-2">
            <p className="font-mediumtext-foreground/60">
              Currículo de aproveitamento:
            </p>
            <p>{currentCurriculum?.id}</p>
          </div>
          <div className="flex gap-3">
            <Show when={showCurriculumButton}>
              <div className="min-w-56">
                <ChooseCurriculum
                  disabled={onlyView || isPendingDsiciplineAnalysis}
                  curriculums={curriculums}
                  currentCurriculum={currentCurriculum}
                  setCurrentCurriculum={handleCurrculumChange}
                  formMethods={formMethods}
                />
              </div>
            </Show>
            <Show
              when={
                request?.desiredEducation?.curriculum !== currentCurriculum?.id
              }
            >
              <div className="min-w-56">
                <Button
                  type="button"
                  disabled={isPendingDsiciplineAnalysis || alreadyAnalyzed}
                  onClick={handleDisciplineAnalysis}
                  isLoading={isPendingDsiciplineAnalysis}
                  variant={'default'}
                  className="flex items-center gap-2"
                >
                  Simular com Izzy +
                </Button>
              </div>
            </Show>
          </div>
        </div>
      </Show>
      <ReviewCard
        title={title}
        headerCard={!isStudent}
        bodyClass={clsx({ 'bg-background': isStudent })}
      >
        <Show when={showCurriculumButton || showDisciplineButton}>
          <div className="flex gap-5 border-b border-ring/50 rounded-t-lg px-6 py-3">
            <Show when={showDisciplineButton}>
              <div className="min-w-56">
                <ChooseCourses
                  disabled={onlyView}
                  availableCollegeCourses={curriculumCourses}
                  formMethods={formMethods}
                  variant={variant}
                />
              </div>
            </Show>
          </div>
        </Show>
        <div>
          <div className="pb-3">
            <Show when={isStudent && hasAutomaticCourses}>
              <div className="ms-2 mt-5 flex items-center gap-2">
                <StarGroup className="opacity-50" />
                <p className="text-foreground/50">
                  Disciplinas selecionadas pela IA
                </p>
                <StarGroup className="opacity-50" />
              </div>
            </Show>
            {(formMethodsIA?.fields?.length || 0) > 0 && (
              <Table className="table-fixed border-spacing-y-2 px-7 pb-0">
                <CoursesEquivalencesTableHeader
                  showStatusColumn={showStatusColumn}
                />
                <TableBody>
                  {formMethodsIA?.fields?.map((field, index) => {
                    const canCreateNewPreviousCourse = false
                    return (
                      <CoursesEquivalencesTableRow
                        variant={variant}
                        key={field.id}
                        fieldKey={'automaticCourseEquivalences'}
                        formMethods={formMethods}
                        index={index}
                        editablePreviousCourses={true}
                        editableDesiredCourses={curriculumChanged}
                        curriculumCourses={curriculumCourses}
                        availableCollegeCourses={curriculumCourses}
                        availablePreviousCourses={previousCourses}
                        onReview={onReview}
                        showStatusColumn={showStatusColumn}
                        canCreateNewPreviousCourse={canCreateNewPreviousCourse}
                      />
                    )
                  })}
                </TableBody>
              </Table>
            )}

            <Show when={!hasAutomaticCourses || hasCourseEquivalences}>
              <Show when={variant === 'student'}>
                <p className="ms-9 text-foreground/50 mt-5">
                  Disciplinas adicionadas manualmente
                </p>
              </Show>
              <Table className="table-fixed border-spacing-y-2 px-7 pb-0">
                <CoursesEquivalencesTableHeader
                  showStatusColumn={showStatusColumn}
                />
                <TableBody>
                  {formMethods?.fields?.map((field, index) => {
                    const editableDesiredCourse = canEditDesiredCourse()
                    return (
                      <CoursesEquivalencesTableRow
                        variant={variant}
                        key={field.id}
                        fieldKey="courseEquivalences"
                        formMethods={formMethods}
                        index={index}
                        editablePreviousCourses={!onlyView}
                        editableDesiredCourses={
                          editableDesiredCourse && !onlyView
                        }
                        curriculumCourses={curriculumCourses}
                        availableCollegeCourses={curriculumCourses}
                        availablePreviousCourses={previousCourses}
                        onReview={onReview}
                        showStatusColumn={showStatusColumn}
                        requestStatus={request?.requestStatus?.id}
                      />
                    )
                  })}
                </TableBody>
              </Table>
              <Show when={!hasCourseEquivalences && !onlyView}>
                <div className="w-full flex justify-center py-10 text-foreground/50">
                  Adicione uma disciplina
                </div>
              </Show>
            </Show>
          </div>
        </div>
        {footer}
      </ReviewCard>
    </div>
  )
}
